import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { fetchCustomerAccountById } from "./customerAccountService";
import "../assets/css/ViewCustomer.css";

function display(value) {
  if (value == null) return "—";
  const s = String(value).trim();
  return s === "" ? "—" : s;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function ViewCustomerAccount() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchCustomerAccountById(id);
        if (!cancelled) setRow(data || null);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || "Could not load client account details.");
          setRow(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const InfoItem = ({ label, value }) => {
    const v = value == null ? "" : String(value);
    const hasValue = v.trim() !== "" && v.trim() !== "—";
    return (
      <div className="viewlead-info-item">
        <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
        <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{hasValue ? v : "—"}</span>
      </div>
    );
  };

  return (
    <div className="viewlead-container">
      <div className="viewlead-header">
        <button
          type="button"
          className="viewlead-back-link"
          onClick={() => navigate("/finance-management", { state: { financeTab: "customers" } })}
        >
          <FaArrowLeft /> Back
        </button>
        <button
          type="button"
          className="btn-edit-lead"
          onClick={() => navigate(`/finance-management/payable/customer-account/${id}/edit`)}
        >
          Edit Client Account
        </button>
      </div>

      {loading ? (
        <div className="viewlead-empty">Loading client account details...</div>
      ) : error ? (
        <div className="viewlead-empty">{error}</div>
      ) : !row ? (
        <div className="viewlead-empty">Client account not found.</div>
      ) : (
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "stretch",
            flexWrap: "wrap",
            marginBottom: 20
          }}
        >
          <div className="viewlead-card" style={{ flex: "2 1 0", minWidth: 260 }}>
            <h3 className="viewlead-section-title">Client Account Information</h3>
            <div className="viewlead-info-grid">
              <InfoItem label="Client Account Id" value={display(row.Customer_Account_Id ?? row.customer_account_id)} />
              <InfoItem label="Client Id" value={display(row.client_id ?? row.client_id)} />
              <InfoItem label="Account Holder Name" value={display(row.Account_Holder_Name ?? row.account_holder_name)} />
              <InfoItem label="Account Number" value={display(row.Account_Number ?? row.account_number)} />
              <InfoItem label="Account Type" value={display(row.Account_Type ?? row.account_type)} />
              <InfoItem label="Bank Name" value={display(row.Bank_Name ?? row.bank_name)} />
              <InfoItem label="Branch" value={display(row.Branch ?? row.branch)} />
              <InfoItem label="IFSC Code" value={display(row.IFSC_Code ?? row.ifsc_code)} />
              <InfoItem label="UPI Id" value={display(row.UPI_Id ?? row.upi_id)} />
              <InfoItem label="Status" value={display(row.Status ?? row.status)} />
              <InfoItem
                label="Primary"
                value={(row.Is_Primary === true || row.Is_Primary === 1 || row.is_primary === 1) ? "Yes" : "No"}
              />
              <InfoItem label="Notes" value={display(row.Notes ?? row.notes)} />
            </div>
          </div>

          <div className="viewlead-card" style={{ flex: "1 1 0", minWidth: 260, maxWidth: 480 }}>
            <h3 className="viewlead-section-title">Audit Information</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 0 }}>
              <InfoItem label="Created By" value={display(row.Created_By_Name ?? row.created_by_name ?? row.Created_By ?? row.created_by)} />
              <InfoItem label="Created On" value={formatDate(row.Created_On ?? row.created_on)} />
              <InfoItem label="Updated By" value={display(row.Updated_By_Name ?? row.updated_by_name ?? row.Updated_By ?? row.updated_by)} />
              <InfoItem label="Updated On" value={formatDate(row.Updated_On ?? row.updated_on)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewCustomerAccount;
